"use client"
/* */
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import { ClearPropsContext } from '../ClearPropsContext/ClearPropsContext.mjs';
import '../../lib/propsContext/propsContext.mjs';
import { dynamic } from '../../lib/propsContext/dynamicProps/dynamic.mjs';
import { PropsContextProvider } from '../../lib/propsContext/PropsContextProvider.mjs';
import { flowComponent } from '../../lib/componentFactory/flowComponent.mjs';
import styles from './PasswordCreationField.module.scss.mjs';
import * as Aria from 'react-aria-components';
import { FieldErrorContext } from 'react-aria-components';
import formFieldStyles from '../FormField/FormField.module.scss.mjs';
import clsx from 'clsx';
import { TunnelProvider, TunnelExit } from '@mittwald/react-tunnel';
import '../Action/Action.mjs';
import { getStateFromLatestPolicyValidationResult } from './lib/getStateFromLatestPolicyValidationResult.mjs';
import locales from '../../../../_virtual/_.locale.json@95341064edeb0e38b66d786dbd62955c.mjs';
import { useLocalizedStringFormatter } from 'react-aria';
import { generateValidationTranslation } from './lib/generateValidationTranslation.mjs';
import { FieldError } from '../FieldError/FieldError.mjs';
import { FieldDescription } from '../FieldDescription/FieldDescription.mjs';
import { ComplexityIndicator } from './components/ComplexityIndicator/ComplexityIndicator.mjs';
import '@mittwald/password-tools-js/policy';
import '@mittwald/password-tools-js/rules';
import { useDebounceValue } from 'usehooks-ts';
import { generatePassword } from './worker/generatePassword.mjs';
import { TogglePasswordVisibilityButton } from './components/TogglePasswordVisibilityButton/TogglePasswordVisibilityButton.mjs';
import { useAbortablePromise } from '../../lib/promises/useAbortablePromise.mjs';
import { defaultPasswordCreationPolicy } from './defaultPasswordCreationPolicy.mjs';
import { Wrap } from '../Wrap/Wrap.mjs';
import { ReactAriaControlledValueFix } from '../../lib/react/ReactAriaControlledValueFix.mjs';
import 'mobx';
import '../../lib/controller/overlay/context.mjs';
import { useIsMounted } from '../../lib/hooks/useIsMounted.mjs';
import '@react-aria/utils';
import 'remeda';
import { ValidationResultButton } from './components/ValidationResultButton/ValidationResultButton.mjs';
import { PasswordGenerateButton } from './components/PasswordGenerateButton/PasswordGenerateButton.mjs';

const validationDebounceMilliseconds = 200;
const PasswordCreationField = flowComponent(
  "PasswordCreationField",
  (props) => {
    const {
      children,
      className,
      ref,
      isDisabled,
      onChange,
      isInvalid: invalidFromProps,
      validationPolicy = defaultPasswordCreationPolicy,
      isRequired,
      value: ignoredValue,
      defaultValue: ignoredDefaultValue,
      ...rest
    } = props;
    const translate = useLocalizedStringFormatter(locales);
    const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const initialPolicyValidationState = {
      isValid: false,
      complexity: {
        min: validationPolicy.minComplexity,
        actual: 4,
        warning: null
      },
      ruleResults: []
    };
    const [policyValidationResult, setPolicyValidationResult] = useState(
      initialPolicyValidationState
    );
    const stateFromValidationResult = getStateFromLatestPolicyValidationResult(
      policyValidationResult
    );
    let latestValidationErrorText = void 0;
    if (stateFromValidationResult) {
      const [translationKey, translationValues] = generateValidationTranslation(
        stateFromValidationResult
      );
      latestValidationErrorText = translate.format(
        translationKey,
        translationValues
      );
    }
    const isMounted = useIsMounted();
    const [value, setValue] = useState("");
    const [bouncedValue, setDebouncedValue] = useDebounceValue(void 0, validationDebounceMilliseconds);
    const isEmptyValue = bouncedValue === void 0;
    const isValidFromValidationResult = !isEmptyValue && stateFromValidationResult?.isValid;
    const isInvalidFromValidationResult = !isEmptyValue && !stateFromValidationResult?.isValid;
    const isInvalid = invalidFromProps || isInvalidFromValidationResult;
    const setUncontrolledValue = (value2) => {
      setValue(value2);
      setDebouncedValue(value2 === "" ? void 0 : value2);
    };
    useEffect(() => {
      if (!ignoredValue) {
        setUncontrolledValue("");
      }
    }, [ignoredValue]);
    const setOptimisticPolicyValidationResult = (isValid = true) => {
      setPolicyValidationResult({
        ...initialPolicyValidationState,
        isValid
      });
    };
    useAbortablePromise(
      async (signal) => {
        if (signal.aborted) {
          return;
        }
        const validationResult = validationPolicy.validate(value);
        if (typeof validationResult.isValid === "boolean") {
          setIsLoading(false);
          setPolicyValidationResult((old) => ({
            ...old,
            isValid: validationResult.isValid,
            ruleResults: validationResult.ruleResults,
            complexity: validationResult.complexity
          }));
          if (onChange && isMounted) {
            onChange(validationResult.isValid ? value : "");
          }
          return;
        }
        const nonPromiseValidationsInvalid = validationResult.ruleResults.filter((r) => !(r instanceof Promise)).some((r) => !r.isValid);
        if (!isEmptyValue) {
          setIsLoading(true);
        }
        if (!nonPromiseValidationsInvalid) {
          setOptimisticPolicyValidationResult();
        }
        void Promise.all([
          Promise.resolve(value),
          ...validationResult.ruleResults
        ]).then(([resolvedValue, ...validationResults]) => {
          if (signal.aborted) {
            return;
          }
          const isValid = !validationResults.some((r) => !r.isValid);
          setIsLoading(false);
          setPolicyValidationResult((old) => ({
            ...old,
            isValid,
            ruleResults: validationResults
          }));
          if (onChange && isMounted) {
            onChange(isValid ? resolvedValue : "");
          }
        });
      },
      [bouncedValue]
    );
    const onChangeValueHandler = (value2) => {
      setUncontrolledValue(value2);
    };
    const onPasswordGenerateHandler = async () => {
      const generatedPassword = await generatePassword(validationPolicy);
      setOptimisticPolicyValidationResult();
      setIsPasswordRevealed(true);
      onChangeValueHandler(generatedPassword);
    };
    const onPasswordInputChangeHandler = (value2) => {
      onChangeValueHandler(value2);
    };
    const onPasswordPasteHandler = (event) => {
      const pastedValue = event.clipboardData.getData("text");
      if (pastedValue !== value) {
        setOptimisticPolicyValidationResult("indeterminate");
      }
    };
    const togglePasswordVisibilityHandler = () => {
      setIsPasswordRevealed((old) => !old);
    };
    const propsContext = {
      Button: {
        tunnelId: "button",
        size: "m",
        variant: "plain",
        color: "secondary",
        isDisabled,
        className: styles.button
      },
      CopyButton: {
        tunnelId: "button",
        size: "m",
        variant: "plain",
        color: "secondary",
        isDisabled,
        className: styles.button
      },
      Label: {
        className: formFieldStyles.label,
        tunnelId: "label",
        optional: !isRequired,
        isDisabled,
        children: dynamic((localProps) => {
          return /* @__PURE__ */ jsxs(Fragment, { children: [
            localProps.children,
            /* @__PURE__ */ jsx(
              PasswordGenerateButton,
              {
                isDisabled,
                onGeneratePasswordAction: onPasswordGenerateHandler
              }
            ),
            /* @__PURE__ */ jsx(
              ValidationResultButton,
              {
                isEmptyValue,
                isDisabled,
                policyValidationResult
              }
            )
          ] });
        })
      },
      FieldDescription: {
        className: formFieldStyles.fieldDescription
      },
      FieldError: {
        className: formFieldStyles.customFieldError,
        children: dynamic(() => {
          if (latestValidationErrorText) {
            return latestValidationErrorText;
          }
        })
      }
    };
    return /* @__PURE__ */ jsx(ClearPropsContext, { children: /* @__PURE__ */ jsx(TunnelProvider, { children: /* @__PURE__ */ jsxs(
      Aria.TextField,
      {
        ...rest,
        type: isPasswordRevealed ? "text" : "password",
        value,
        onChange: onPasswordInputChangeHandler,
        onPaste: onPasswordPasteHandler,
        className: clsx(className, formFieldStyles.formField),
        isDisabled,
        isInvalid,
        isRequired,
        children: [
          /* @__PURE__ */ jsx(TunnelExit, { id: "label" }),
          /* @__PURE__ */ jsxs(
            Aria.Group,
            {
              isDisabled,
              className: clsx(styles.inputGroup),
              children: [
                /* @__PURE__ */ jsx(
                  ReactAriaControlledValueFix,
                  {
                    inputContext: Aria.InputContext,
                    props,
                    children: /* @__PURE__ */ jsx(Aria.Input, { className: styles.input, ref })
                  }
                ),
                /* @__PURE__ */ jsxs(Aria.Group, { className: styles.buttonContainer, children: [
                  /* @__PURE__ */ jsx(
                    TogglePasswordVisibilityButton,
                    {
                      className: styles.button,
                      isVisible: isPasswordRevealed,
                      isDisabled,
                      onPress: togglePasswordVisibilityHandler
                    }
                  ),
                  /* @__PURE__ */ jsx(TunnelExit, { id: "button" })
                ] }),
                /* @__PURE__ */ jsx(
                  ComplexityIndicator,
                  {
                    isEmptyValue,
                    isLoading,
                    policyValidationResult
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs(PropsContextProvider, { props: propsContext, children: [
            isValidFromValidationResult && /* @__PURE__ */ jsx(FieldDescription, { children: latestValidationErrorText }),
            isInvalidFromValidationResult && policyValidationResult.isValid !== "indeterminate" && /* @__PURE__ */ jsx(FieldError, { children: latestValidationErrorText }),
            /* @__PURE__ */ jsx(Wrap, { if: isInvalidFromValidationResult, children: /* @__PURE__ */ jsx(
              FieldErrorContext.Provider,
              {
                value: {
                  isInvalid: false,
                  validationErrors: [],
                  validationDetails: {
                    customError: false,
                    valid: true,
                    typeMismatch: false,
                    stepMismatch: false,
                    valueMissing: false,
                    tooShort: false,
                    tooLong: false,
                    rangeUnderflow: false,
                    patternMismatch: false,
                    badInput: false,
                    rangeOverflow: false
                  }
                },
                children
              }
            ) })
          ] })
        ]
      }
    ) }) });
  }
);

export { PasswordCreationField, PasswordCreationField as default };
//# sourceMappingURL=PasswordCreationField.mjs.map
